import { supabase } from "@/utils/supabase";
import { OrderMapper } from "@/adapters/Order.mapper";
import { toB2BError } from "@/utils/b2bErrors";
import { ORDERS_WITHOUT_ROUTE_FILTER } from "@/utils/orderRoute";
import { OrderStatus } from "@/types/order/order.common";
import { OrderPhotosMapper } from "@/adapters/OrderPhotos.mapper";
/**
 * Concrete implementation of OrderService
 * Handles all database operations for orders
 */
export class OrderService {
    /**
     * Load orders with filters and pagination via v_orders_list view.
     * The view provides flat client/vehicle/route/charging-stats fields in one query.
     * Search works via single .or() across orderNumber, address, clientFirstName, clientLastName, clientPhone.
     */
    async getOrdersWithParams(params) {
        try {
            const { filters, page, pageSize, sorters } = params;
            let query = supabase()
                .from("v_orders_list")
                .select("*", { count: "exact" });
            // Status filter
            if (filters?.status && Array.isArray(filters.status) && filters.status.length > 0) {
                query = query.in("status", filters.status);
            }
            // Date filters
            if (filters?.createdDate?.dateFrom && filters.createdDate.dateFrom.trim()) {
                query = query.gte("createdAt", filters.createdDate.dateFrom);
            }
            if (filters?.createdDate?.dateTo && filters.createdDate.dateTo.trim()) {
                query = query.lte("createdAt", `${filters.createdDate.dateTo}T23:59:59.999Z`);
            }
            if (filters?.scheduledDate?.dateFrom && filters.scheduledDate.dateFrom.trim()) {
                query = query.gte("scheduledDate", filters.scheduledDate.dateFrom);
            }
            if (filters?.scheduledDate?.dateTo && filters.scheduledDate.dateTo.trim()) {
                query = query.lte("scheduledDate", `${filters.scheduledDate.dateTo}T23:59:59.999Z`);
            }
            // isPaid filter
            if (filters?.isPaid !== undefined && filters.isPaid !== null) {
                query = query.eq("isPaid", filters.isPaid);
            }
            // FR-007 (UC-101 BR-8): режимы взаимоисключающие — «физлица» и «все корпоративные» по payerType,
            // конкретная компания — по её id, а не поиском по названию
            if (filters?.payer) {
                query =
                    filters.payer === "PRIVATE" || filters.payer === "COMPANY"
                        ? query.eq("payerType", filters.payer)
                        : query.eq("corpCompanyId", filters.payer);
            }
            // WP-DSP-FIX-03: «без маршрута» — правило вью unassigned_orders (utils/orderRoute)
            if (filters?.withoutRoute) {
                query = query.or(ORDERS_WITHOUT_ROUTE_FILTER);
            }
            // Поиск: простой OR по плоским колонкам представления (клиент уже в join-е)
            if (filters?.search && filters.search.trim()) {
                const q = filters.search.trim();
                query = query.or([
                    `orderNumber.ilike.*${q}*`,
                    `addressShort.ilike.*${q}*`,
                    `addressLocality.ilike.*${q}*`,
                    `clientFirstName.ilike.*${q}*`,
                    `clientLastName.ilike.*${q}*`,
                    `clientPhone.ilike.*${q}*`,
                    `vehicleStateNumber.ilike.*${q}*`,
                ].join(","));
            }
            // Сортировка
            if (sorters && Object.keys(sorters).length > 0) {
                Object.entries(sorters).forEach(([field, order]) => {
                    if (order === "ascend")
                        query = query.order(field, { ascending: true });
                    else if (order === "descend")
                        query = query.order(field, { ascending: false });
                });
            }
            else {
                query = query.order("scheduledDate", { ascending: false });
            }
            const from = (page - 1) * pageSize;
            query = query.range(from, from + pageSize - 1);
            const { data, error, count } = await query;
            if (error)
                throw error;
            const orders = data.map((raw) => OrderMapper.toDomain(raw));
            return {
                data: orders,
                total: count || 0,
                page,
                pageSize,
            };
        }
        catch (error) {
            console.error("Error in getOrdersWithParams:", error);
            throw error;
        }
    }
    async getOrdersInRoute(routeId) {
        const { data, error } = await supabase()
            .from("orders")
            .select(`
        *,
        route_points!inner(orderIndex, idRoute)
        `)
            .eq("route_points.idRoute", routeId)
            .order("orderIndex", {
            referencedTable: "route_points",
            ascending: true,
        });
        const orders = (data ?? []).map((o) => {
            const { route_points, ...order } = o;
            return order;
        });
        if (error) {
            throw new Error(`Failed to load orders: ${error.message}`);
        }
        return orders.map((order) => OrderMapper.toDomain(order));
    }
    /**
     * Load a specific order with all related data
     */
    async getOrder(id) {
        const { data, error } = await supabase()
            .from("orders")
            .select(`*`)
            .eq("id", id)
            .eq("isDeleted", false)
            .single();
        if (error) {
            throw new Error(`Failed to load order: ${error.message}`);
        }
        if (!data) {
            throw new Error("Order not found");
        }
        // WP-DSP-FIX-03: в таблице orders нет колонок маршрута — без них routeInfo был пуст для любого маршрута
        // (карточка писала «не назначен» и при маршруте CREATED). Маршрут берём из v_orders_list.
        const routeFields = await this.getOrderRouteFields(id);
        // Convert DTO to domain object
        return OrderMapper.toDomain({ ...data, ...routeFields });
    }
    /** Последний неудалённый маршрут заказа (`routeId`, `routePlannedStartTime`, `routeStatus`) из v_orders_list. */
    async getOrderRouteFields(id) {
        const { data, error } = await supabase()
            .from("v_orders_list")
            .select("routeId, routePlannedStartTime, routeStatus")
            .eq("id", id)
            .maybeSingle();
        if (error) {
            throw new Error(`Failed to load order route: ${error.message}`);
        }
        return data ?? {};
    }
    /**
     * Create a new order
     */
    async createOrder(orderData) {
        // Generate order number
        const orderNumber = await this.generateOrderNumber();
        // Convert domain object to DTO for database insertion
        const orderDto = OrderMapper.toCreateOrderDto({
            ...orderData,
            orderNumber,
            status: OrderStatus.NEW,
            idSubscription: null,
        });
        // FR-007 (§3.3): в мок-сборке маркер в адресе корп-заказа даёт отказ в форме ответа PostgREST,
        // чтобы разбор ошибки проверялся тем же кодом, что и на живых данных. Частный заказ маркер не
        // останавливает — после «Оформить как физлицо» тот же заказ создаётся (BR-ORD-07).
        // Литерал — не через featureFlags: иначе webpack не выбросит ветку из боевой сборки (G4).
        if (process.env.REACT_APP_B2B_MOCK === "true" && orderDto.corp_company_id) {
            const { corpRefusalForPayload } = await import("./mock/corpOrderRefusals");
            const refusal = corpRefusalForPayload(orderDto);
            if (refusal)
                throw toB2BError(refusal);
        }
        const { data, error } = await supabase()
            .from("orders")
            .insert(orderDto)
            .select()
            .single();
        if (error) {
            // FR-007: код и details отказа обязаны дойти до экрана (CP001–CP016),
            // иначе диспетчер видит «Failed to create order: …» вместо причины.
            throw toB2BError(error);
        }
        // Create initial status history
        await this.addStatusHistory(data.id, OrderStatus.NEW, "Order created");
        // D31б (WP-DSP-FIX-02): связь с автомобилем клиента — как у create_order клиентского приложения
        await this.saveOrderVehicle(data.id, orderData.idVehicle ?? null);
        // Convert DTO back to domain object
        return OrderMapper.toDomain(data);
    }
    /**
     * Пишет/обновляет строку `order_vehicles` заказа (одна активная строка на заказ). Без автомобиля ничего не делает:
     * старые заказы без автомобиля редактировать можно (WP-DSP-FIX-02 п. 2).
     */
    async saveOrderVehicle(orderId, vehicleId) {
        if (!vehicleId)
            return;
        const { data: vehicle, error: vehicleError } = await supabase()
            .from("vehicles_clients")
            .select("id,stateNumber")
            .eq("id", vehicleId)
            .maybeSingle();
        if (vehicleError)
            throw toB2BError(vehicleError);
        const stateNumber = vehicle?.stateNumber ?? null;
        const { data: existing, error: existingError } = await supabase()
            .from("order_vehicles")
            .select("id,idVehicle")
            .eq("idOrder", orderId)
            .eq("isDeleted", false)
            .order("createdAt", { ascending: false })
            .limit(1)
            .maybeSingle();
        if (existingError)
            throw toB2BError(existingError);
        if (existing) {
            if (existing.idVehicle === vehicleId)
                return;
            const { error } = await supabase()
                .from("order_vehicles")
                .update({ idVehicle: vehicleId, stateNumber })
                .eq("id", existing.id);
            if (error)
                throw toB2BError(error);
            return;
        }
        const { error } = await supabase()
            .from("order_vehicles")
            .insert({ idOrder: orderId, idVehicle: vehicleId, stateNumber });
        if (error)
            throw toB2BError(error);
    }
    /**
     * Update an existing order
     */
    async updateOrder(orderId, updateData) {
        // FR-007: тело UPDATE без полей плательщика и снимка — полный DTO корп-заказа
        // означал бы смену плательщика (CP008), хотя пользователь ничего не менял.
        const orderDto = OrderMapper.toUpdateOrderDto(updateData);
        if (process.env.REACT_APP_B2B_MOCK === "true") {
            const { corpRefusalForPayload } = await import("./mock/corpOrderRefusals");
            const refusal = corpRefusalForPayload(orderDto);
            if (refusal)
                throw toB2BError(refusal);
        }
        const { data, error } = await supabase()
            .from("orders")
            .update(orderDto)
            .eq("id", orderId)
            .select()
            .single();
        if (error) {
            // FR-007: CP008 / CP003 (заморозка дат) / CP004 / CP006 — текстом, не сырой ошибкой
            throw toB2BError(error);
        }
        // Convert DTO back to domain object
        // D31б: выбранный при редактировании автомобиль записывается в order_vehicles
        await this.saveOrderVehicle(orderId, updateData.idVehicle ?? null);
        return OrderMapper.toDomain(data);
    }
    /**
     * Soft delete an order
     */
    async deleteOrder(orderId) {
        const { error } = await supabase()
            .from("orders")
            .update({ isDeleted: true })
            .eq("id", orderId);
        if (error) {
            // FR-007 (UC-107): CP013 — у корп-заказа с действующим начислением
            throw toB2BError(error);
        }
    }
    /**
     * Change order status with history tracking
     */
    async changeOrderStatus(id, status, reason, technicianId) {
        // 1. Get current status to verify transition
        const { data: currentOrder, error: fetchError } = await supabase()
            .from("orders")
            .select("status")
            .eq("id", id)
            .single();
        if (fetchError || !currentOrder) {
            throw new Error("Order not found");
        }
        const previousStatus = currentOrder.status;
        // 2. Update status
        const { error: updateError } = await supabase()
            .from("orders")
            .update({ status: status })
            .eq("id", id);
        if (updateError) {
            // FR-007 (UC-105): CP009 / CP010 / CP012 у корп-заказа — текстом, не сырой ошибкой
            throw toB2BError(updateError);
        }
        // 3. Add to history
        await this.addStatusHistory(id, status, reason, technicianId, previousStatus);
    }
    async getOrderPhotos(id) {
        const { data, error } = await supabase()
            .from("order_photos")
            .select("*")
            .eq("order_id", id);
        if (error) {
            throw new Error(`Failed to load photos: ${error.message}`);
        }
        return (data || []).map(OrderPhotosMapper.toDomain);
    }
    /**
     * Получить заказы без привязки к маршруту (нераспределённые)
     * Использует SQL View с LEFT JOIN route_points WHERE rp.id IS NULL
     */
    async getUnassignedOrders() {
        const { data, error } = await supabase()
            .from("unassigned_orders")
            .select("*");
        if (error) {
            throw new Error(`Failed to load unassigned orders: ${error.message}`);
        }
        return (data ?? []).map((order) => OrderMapper.toDomain(order));
    }
    // --- Private Helpers ---
    async generateOrderNumber() {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        // Count orders for today
        const { count } = await supabase()
            .from("orders")
            .select("*", { count: "exact", head: true })
            .gte("createdAt", `${year}-${month}-${day}T00:00:00.000Z`)
            .lte("createdAt", `${year}-${month}-${day}T23:59:59.999Z`);
        const orderNumber = `ORD-${year}${month}${day}-${String((count || 0) + 1).padStart(3, "0")}`;
        return orderNumber;
    }
    async addStatusHistory(orderId, newStatus, reason, technicianId, previousStatus) {
        const { error } = await supabase().from("order_statuses").insert({
            idOrder: orderId,
            newStatus: newStatus,
            previousStatus: previousStatus,
            reason: reason,
            idTechnician: technicianId,
            createdAt: new Date().toISOString(),
            isDeleted: false,
        });
        if (error) {
            console.error("Failed to save status history:", error);
            // Non-blocking error
        }
    }
}
export const orderService = new OrderService();
